import React from "react";

import type { WitnessBindingMode } from "@/components/OmniCine/Timeline/MediaUnion";

export interface ImportWitnessCapsuleProps {
  id: string;
  mediaType: "audio" | "video" | "image";
  label: string;
  durationSec: number;
  currentTimeSec?: number;
  isFocused: boolean;
  isSelected: boolean;
  isPinned?: boolean;
  status?: "idle" | "ready" | "playing" | "muted" | "error";
  sourceId: string;
  clipId?: string;
  tickerText?: string;
  tickerTone?: "blue" | "orange" | "red";
  bindingMode?: WitnessBindingMode;
  hasLinkedAudio?: boolean;
  hasLinkedVideo?: boolean;
  onFocus: (id: string) => void;
  onOpenWitnessBar: (id: string) => void;
  onDismiss?: (id: string) => void;
}

function fmt(sec: number | undefined): string {
  const safe = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const mm = Math.floor(safe / 60);
  const ss = Math.floor(safe % 60)
    .toString()
    .padStart(2, "0");
  return `${mm}:${ss}`;
}

function statusLabel(status: ImportWitnessCapsuleProps["status"]): string {
  switch (status) {
    case "playing":
      return "LIVE";
    case "muted":
      return "MUTED";
    case "error":
      return "ERROR";
    case "idle":
      return "IDLE";
    default:
      return "READY";
  }
}

function bindingModeLabel(
  bindingMode: WitnessBindingMode | undefined,
  mediaType: ImportWitnessCapsuleProps["mediaType"],
): string {
  switch (bindingMode) {
    case "linked":
      return mediaType === "audio" ? "SONIC LINK" : mediaType === "image" ? "STILL LINK" : "A/V LINK";
    case "overrideAudio":
      return "AUDIO SWAP";
    case "videoOnly":
      return mediaType === "image" ? "STILL HOLD" : "SILENT CUT";
    case "unbound":
      return "UNROUTED";
    default:
      return mediaType === "audio" ? "SONIC" : mediaType === "image" ? "STILL" : "DIRECT CUT";
  }
}

function linkStateLabel(hasLinkedAudio: boolean, hasLinkedVideo: boolean): string {
  if (hasLinkedAudio && hasLinkedVideo) return "A+V ROUTE";
  if (hasLinkedAudio) return "AUDIO ROUTE";
  if (hasLinkedVideo) return "VISUAL ROUTE";
  return "SOLO";
}

function bindingTone(bindingMode: WitnessBindingMode | undefined, status: ImportWitnessCapsuleProps["status"]): {
  background: string;
  border: string;
  color: string;
} {
  if (status === "error") {
    return {
      background: "rgba(127, 29, 29, 0.34)",
      border: "1px solid rgba(248,113,113,0.3)",
      color: "rgba(254, 202, 202, 0.96)",
    };
  }

  if (bindingMode === "overrideAudio" || bindingMode === "videoOnly" || bindingMode === "unbound") {
    return {
      background: "rgba(120, 53, 15, 0.3)",
      border: "1px solid rgba(251,146,60,0.24)",
      color: "rgba(255,237,213,0.94)",
    };
  }

  return {
    background: "rgba(8, 47, 73, 0.3)",
    border: "1px solid rgba(56,189,248,0.2)",
    color: "rgba(224,242,254,0.94)",
  };
}

export default function ImportWitnessCapsule({
  id,
  mediaType,
  label,
  durationSec,
  currentTimeSec,
  isFocused,
  isSelected,
  isPinned = false,
  status = "ready",
  bindingMode,
  hasLinkedAudio = false,
  hasLinkedVideo = false,
  onFocus,
  onOpenWitnessBar,
  onDismiss,
}: ImportWitnessCapsuleProps): JSX.Element {
  const showCurrent = typeof currentTimeSec === "number" && Number.isFinite(currentTimeSec);
  const shortLabel = label.length > 18 ? `${label.slice(0, 15).trimEnd()}...` : label;
  const capsuleBorder = isFocused || isSelected ? "1px solid rgba(125, 211, 252, 0.46)" : "1px solid rgba(255,255,255,0.07)";
  const capsuleBackground = isFocused || isSelected
    ? "linear-gradient(180deg, rgba(14, 116, 144, 0.32), rgba(15, 23, 42, 0.78))"
    : "linear-gradient(180deg, rgba(20, 28, 38, 0.48), rgba(12, 18, 28, 0.72))";
  const activeGlow = status === "playing"
    ? "radial-gradient(circle at center, rgba(16,185,129,0.95), rgba(16,185,129,0.18) 62%, rgba(16,185,129,0) 72%)"
    : status === "error"
      ? "radial-gradient(circle at center, rgba(248,113,113,0.96), rgba(248,113,113,0.18) 62%, rgba(248,113,113,0) 72%)"
      : "radial-gradient(circle at center, rgba(125,211,252,0.92), rgba(125,211,252,0.14) 62%, rgba(125,211,252,0) 72%)";
  const bindingVisual = bindingTone(bindingMode, status);
  const bindingLabel = bindingModeLabel(bindingMode, mediaType);
  const linkedLabel = linkStateLabel(hasLinkedAudio, hasLinkedVideo);
  const actionLabel = mediaType === "audio" ? "Listen" : mediaType === "image" ? "Review" : "Witness";

  return (
    <div
      data-oc-import-witness-capsule={id}
      className="inline-flex min-w-0 items-center gap-1.5 rounded-full px-1.5 py-1"
      style={{
        background: capsuleBackground,
        border: capsuleBorder,
        boxShadow: isFocused || isSelected ? "0 8px 18px rgba(8, 145, 178, 0.14)" : "0 6px 14px rgba(0, 0, 0, 0.12)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
    >
      <span
        className="inline-flex h-5 w-5 flex-none items-center justify-center rounded-full"
        aria-hidden
        style={{ background: activeGlow }}
      >
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{
            background:
              status === "playing"
                ? "rgba(16,185,129,0.98)"
                : status === "error"
                  ? "rgba(248,113,113,0.98)"
                  : "rgba(224,242,254,0.92)",
            boxShadow:
              status === "playing"
                ? "0 0 10px rgba(16,185,129,0.85)"
                : status === "error"
                  ? "0 0 10px rgba(248,113,113,0.75)"
                  : "0 0 8px rgba(125,211,252,0.5)",
          }}
        />
      </span>

      <button
        type="button"
        onClick={() => onFocus(id)}
        className="flex min-w-0 items-center gap-1.5 rounded-full px-1 py-0.5 text-left"
        aria-pressed={isFocused || isSelected}
        aria-label={`Focus ${label}`}
        style={{
          color: "rgba(255,255,255,0.96)",
          outline: "none",
        }}
      >
        <span
          className="inline-flex h-5 items-center rounded-full px-2 text-[9px] font-extrabold tracking-[0.18em]"
          style={{
            background: mediaType === "audio"
              ? "linear-gradient(180deg, rgba(16,185,129,0.96), rgba(5,150,105,0.96))"
              : mediaType === "image"
                ? "linear-gradient(180deg, rgba(251,191,36,0.96), rgba(217,119,6,0.96))"
                : "linear-gradient(180deg, rgba(96,165,250,0.96), rgba(37,99,235,0.96))",
          }}
        >
          {mediaType === "audio" ? "AUDIO" : mediaType === "image" ? "IMAGE" : "VIDEO"}
        </span>

        <span className="min-w-0">
          <span className="block truncate text-[11px] font-semibold leading-4 text-white/92">{shortLabel}</span>
          <span className="block text-[9px] font-medium uppercase tracking-[0.12em] text-white/54">
            {showCurrent ? `${fmt(currentTimeSec)} / ${fmt(durationSec)}` : `0:00 / ${fmt(durationSec)}`}
          </span>
        </span>
      </button>

      <span
        className="inline-flex h-5 items-center rounded-full px-1.5 text-[9px] font-bold tracking-[0.12em]"
        aria-hidden
        style={{
          background: status === "playing" ? "rgba(16,185,129,0.14)" : "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.06)",
          color: status === "error" ? "rgba(248,113,113,0.92)" : "rgba(255,255,255,0.74)",
        }}
      >
        {statusLabel(status)}
      </span>

      <span
        className="inline-flex h-5 items-center rounded-full px-1.5 text-[9px] font-bold tracking-[0.12em]"
        aria-label={`Binding mode ${bindingLabel}`}
        style={bindingVisual}
      >
        {bindingLabel}
      </span>

      <span
        className="inline-flex h-5 items-center rounded-full px-1.5 text-[9px] font-bold tracking-[0.12em]"
        aria-label={`Linked route ${linkedLabel}`}
        style={{
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.74)",
        }}
      >
        {linkedLabel}
      </span>

      {isPinned && (
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100/80" aria-hidden>
          PIN
        </span>
      )}

      <button
        type="button"
        onClick={() => onOpenWitnessBar(id)}
        className="inline-flex h-6 items-center rounded-full px-2 text-[9px] font-bold uppercase tracking-[0.18em]"
        aria-label={`Open witness bar for ${label}`}
        style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.06)",
          color: "rgba(255,255,255,0.72)",
        }}
      >
        {actionLabel}
      </button>

      {onDismiss && (
        <button
          type="button"
          onClick={() => onDismiss(id)}
          className="inline-flex h-7 items-center rounded-full px-2 text-[11px] font-bold text-white/60"
          aria-label={`Remove media for ${label}`}
          title="Remove media"
          style={{
            background: "transparent",
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}